import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import { Accordion } from '@winnin/winnin_ui.components.accordion'
import { SelectableListItem } from '@winnin/winnin_ui.components.selectable-list-item'

import { JobFolderAccordionItensSkeleton } from 'components/AddToJobFolderModal/components/AddToJobFolderModalSkeleton'
import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { TOLGEE_GLOBALS, analyticsTracker } from 'utils'

import { JobTitle } from './components/JobTitle'
import { NewFolderInput } from './components/NewFolderInput'
import { CREATE_JOB_FOLDER } from './query'

import './styles.scss'

/**
 * @param {Object} props
 * @param {JobWithFolders} props.job
 * @param {Folder[]} props.folders
 * @param {boolean} props.isActive
 * @param {number} [props.foldersLimit]
 * @param {boolean} [props.isLoading]
 * @param {(event: SyntheticEvent<Element, Event>, isExpanded: boolean) => void} props.onCollapse
 * @param {(selectedFolder: Folder | null) => void} props.onSelectFolder
 */
export const JobFolderAccordion = props => {
  const { folders, isActive, job, foldersLimit, isLoading, onCollapse, onSelectFolder } = props

  const t = useTranslate()

  const [selectedFolder, setSelectedFolder] = useState({ id: null, jobId: job.id })

  const [createFolder] = useMutation(CREATE_JOB_FOLDER, {
    onError: error => {
      console.error('error @CREATE_JOB_FOLDER', error)
    },
    refetchQueries: ['workspaceJobsWithFoldersPaginated'],
  })

  const totalFolders = folders?.length
  const hasReachedFoldersLimit = totalFolders >= foldersLimit

  /** @param {Folder} folder */
  const handleSelectFolder = useCallback(
    folder => {
      setSelectedFolder(folder)
      onSelectFolder?.(folder)
    },
    [onSelectFolder]
  )

  const handleCreateFolder = useCallback(
    async folderTitle => {
      const workspaceName = PanelStore?.selectedPanel?.name
      const { CREATE_JOB_FOLDER } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(CREATE_JOB_FOLDER, {
        'Folder name': folderTitle,
        jobId: job?.id,
        workspaceName,
      })

      const response = await createFolder({
        variables: {
          createWorkspaceJobFolderInput: {
            name: folderTitle,
            workspaceJobId: job?.id,
          },
        },
        update: (cache, { data }) => {
          const createdFolder = data?.createWorkspaceJobFolder
          const cacheId = `WorkspaceJob:${job.id}`

          cache.modify({
            id: cacheId,
            fields: {
              foldersPaginated: foldersPaginated => {
                const foldersList = foldersPaginated?.data
                return {
                  ...foldersPaginated,
                  data: [...foldersList, createdFolder],
                }
              },
            },
          })
        },
      })

      const createdFolderId = response.data?.createWorkspaceJobFolder?.id

      if (createdFolderId) {
        handleSelectFolder({
          id: createdFolderId,
          jobId: selectedFolder.jobId,
          name: folderTitle,
          pins: { creatorAmount: 0, postAmount: 0, territoriesAmount: 0, totalAmount: 0 },
        })
      }
    },
    [createFolder, handleSelectFolder, job.id, selectedFolder.jobId]
  )

  return (
    <Accordion
      title={<JobTitle isActive={isActive} job={job} />}
      expanded={isActive}
      onChange={onCollapse}
      className={classNames('JobFolderAccordion', { expanded: isActive })}
      maxContentHeight={9999}
    >
      <ul className="JobFolderAccordion-content">
        {isLoading ? (
          <JobFolderAccordionItensSkeleton />
        ) : (
          <>
            <SelectableListItem
              title={t(TOLGEE_GLOBALS.allReferencesShortTitle)}
              subtitle={t({
                ...TOLGEE_GLOBALS.savedReferencesAmount,
                parameters: {
                  pinsAmount: job.pins.totalAmount,
                },
              })}
              onClick={() => handleSelectFolder({ id: null })}
              isActive={selectedFolder.id === null}
            />
            {folders.map(folder => (
              <SelectableListItem
                key={folder.jobId + folder.id}
                title={folder.name}
                subtitle={t({
                  ...TOLGEE_GLOBALS.savedReferencesAmount,
                  parameters: {
                    pinsAmount: folder.pins.totalAmount,
                  },
                })}
                onClick={() => handleSelectFolder(folder)}
                isActive={selectedFolder.id === folder.id}
              />
            ))}
          </>
        )}
      </ul>
      <NewFolderInput
        onCreate={handleCreateFolder}
        disabled={hasReachedFoldersLimit}
        disabledMessage={t(TOLGEE_GLOBALS.foldersLimitMessageAsDashboard)}
        foldersLimit={foldersLimit}
        totalFolders={totalFolders}
      />
    </Accordion>
  )
}
